<template>
  <div class="comments-form">
    <VisualTextEditor v-model:value="newComment"
                      data-locator="add-comment-textarea"
                      :clean-content-on-blur="false"
                      :error-messages="newCommentErrors"
                      :hide-menubar="hideMenubar"
                      :max-length="NEW_COMMENT_MAX_LENGTH"
                      :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.newCommentPlaceholder`)"
                      :taggable-groups="taggableGroups"
                      :taggable-members="taggableUsers"
                      clean-content-on-validation
                      keep-toolbar-on-blur
                      ref="visualTextEditor"
                      @blur="onBlur"
    />
    <div class="d-flex pa-3">
      <app-button data-locator="toggle-toolbar-button"
                  size="small"
                  type="icon"
                  @click="onToggleToolbar"
      >
        <app-icon-ds icon="font-case" weight="far" size="sm" />
      </app-button>
      <app-button data-locator="open-mention-list-button"
                  size="small"
                  type="icon"
                  @click="onOpenMentionList"
      >
        <app-icon-ds icon="at" weight="far" size="xs" />
      </app-button>
      <app-button data-locator="post-comment-button"
                  :disabled="!canPostComment"
                  :loading="disabled"
                  class="post-comment-button ml-auto"
                  size="small"
                  type="icon"
                  @click="onPostComment"
      >
        <app-icon-ds icon="arrow-up" size="xs" color="currentColor" />
      </app-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { escapeHtml } from '@/common/utils/strings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'

const NEW_COMMENT_MAX_LENGTH = 5000
const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPaneCommentsForm'

export default {
  name: 'ChecklistPaneCommentsForm',
  components: { VisualTextEditor },
  emits: ['data-unsaved','post-comment'],
  props: {
    cleared: Boolean,
    disabled: Boolean,
    taggableGroups: {
      type: Array,
      default: () => [],
    },
    taggableUsers: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    NEW_COMMENT_MAX_LENGTH,
    TRANSLATION_KEY_PREFIX,
    hideMenubar: true,
    newComment: '',
  }),
  methods: {
    onClear() {
      this.$refs.visualTextEditor.clear();
    },
    onBlur() {
        this.$emit('data-unsaved', this.newCommentLength ? this.checklistPane?.tab : null)
    },
    onOpenMentionList() {
      this.$refs.visualTextEditor.onOpenMentionList();
    },
    onPostComment() {
      this.$emit('post-comment', this.newComment)
    },
    onToggleToolbar() {
      this.hideMenubar = !this.hideMenubar
    },
  },
  computed: {
    ...mapState('groups', ['groups']),
    ...mapState('checklist', ['checklistPane','deleteTaskCommentPendingIds','postTaskCommentPendingIds']),
    canPostComment () {
      return this.newCommentLength > 0 && !this.newCommentIsTooLong && !this.disabled
    },
    newCommentLength () {
      return escapeHtml(this.newComment).length
    },
    newCommentIsTooLong () {
      return this.newCommentLength > this.NEW_COMMENT_MAX_LENGTH
    },
    newCommentErrors () {
      const errors = []

      if (this.newCommentIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
  },
  watch: {
    cleared (value) {
      if (value) {
        this.onClear()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.comments-form {
  border: 1px solid rgb(var(--v-theme-grey-lighten-3));
  border-radius: 12px;
  overflow: hidden;

  :deep(.editor__content .ProseMirror) {
    border: none !important;
  }
  :deep(.editor__menubar) {
    border: none !important;
    padding: 4px 0;
  }
  .post-comment-button {
    &:disabled {
      background-color: rgb(var(--v-theme-grey-50));
      color: rgb(var(--v-theme-grey-300));
    }
    &:not(:disabled), &:focus {
      background-color: rgb(var(--v-theme-transactional));
      color: white !important;

      &:hover, &:active {
        background-color: rgb(var(--v-theme-blue-600));
      }
    }
  }
}
</style>
